import { useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { useSessionData } from '@confluence/session-data';

import {
	HomeViewsUserPreferencesQuery,
	HomeViewsUserPreferencesMutation,
} from './HomeViewsUserPreferencesQuery.graphql';
import type { HomeViewsUserPreferencesQuery as HomeViewsUserPreferencesQueryType } from './__types__/HomeViewsUserPreferencesQuery';
import { PagesDisplayPersistenceOption } from './__types__/HomeViewsUserPreferencesQuery';
import type {
	HomeViewsUserPreferencesMutation as HomeViewsUserPreferencesMutationType,
	HomeViewsUserPreferencesMutationVariables,
} from './__types__/HomeViewsUserPreferencesMutation';

type UseHomeViewsUserPreference = (defaultPreference?: PagesDisplayPersistenceOption) => {
	loading: boolean;
	queryError?: ApolloError;
	mutationError?: ApolloError;
	appearance: PagesDisplayPersistenceOption;
	updateHomeViewsUserPreference: (appearance: PagesDisplayPersistenceOption) => void;
};

export const useLoggedInHomeViewsUserPreferences: UseHomeViewsUserPreference = (
	defaultPreference = PagesDisplayPersistenceOption.CARDS,
) => {
	const { isLoggedIn } = useSessionData();
	const apolloClient = useApolloClient();
	const {
		data,
		loading,
		error: queryError,
	} = useQuery<HomeViewsUserPreferencesQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeViewsUserPreferencesQuery,
		{
			skip: !isLoggedIn,
		},
	);

	const appearance = data?.userPreferences.homePagesDisplayView || defaultPreference;

	const [homeViewsUserPreferencesMutation, { error: mutationError }] = useMutation<
		HomeViewsUserPreferencesMutationType,
		HomeViewsUserPreferencesMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeViewsUserPreferencesMutation,
	);

	const updateHomeViewsUserPreference = (appearance: PagesDisplayPersistenceOption) => {
		if (isLoggedIn) {
			void homeViewsUserPreferencesMutation({
				variables: { appearance },
				optimisticResponse: {
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						homePagesDisplayView: appearance,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation

			// Always write to the cache so the user sees the preference even if there is an error
			apolloClient.writeQuery<HomeViewsUserPreferencesQueryType>({
				query: HomeViewsUserPreferencesQuery,
				data: {
					userPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						homePagesDisplayView: appearance,
					},
				},
			});
		}
	};

	return {
		loading,
		queryError,
		mutationError,
		appearance,
		updateHomeViewsUserPreference,
	};
};

export const useAnonymousHomeViewsUserPreferences: UseHomeViewsUserPreference = (
	defaultPreference: PagesDisplayPersistenceOption = PagesDisplayPersistenceOption.CARDS,
) => {
	const [appearance, setAppearance] = useState(defaultPreference);
	return {
		loading: false,
		appearance,
		updateHomeViewsUserPreference: setAppearance,
	};
};
